// Catálogo parametrizable de motivos de ajuste. Inventario → Catálogo → Motivos de ajuste.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { AjusteMotivo, Campo, Modal, Switch, TIPO_MOTIVO, fieldCls, inv } from './shared';

const vacio = { codigo: '', nombre: '', tipo: 'ambos' as AjusteMotivo['tipo'], es_activo: true };
const tipoLabel = (t: AjusteMotivo['tipo']) => TIPO_MOTIVO.find((x) => x.value === t)?.label ?? t;

export function MotivosAjuste() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: motivos = [], isFetching } = useQuery<AjusteMotivo[]>({
    queryKey: ['inv-ajuste-motivos'], queryFn: () => inv.list<AjusteMotivo>('ajuste_motivos', '?select=*&order=nombre'),
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (m?: AjusteMotivo) => {
    if (m) { setEditId(m.id); setForm({ codigo: m.codigo ?? '', nombre: m.nombre, tipo: m.tipo, es_activo: m.es_activo }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const body = { codigo: form.codigo || null, nombre: form.nombre.trim(), tipo: form.tipo, es_activo: form.es_activo, updated_at: new Date().toISOString() };
      return editId ? inv.update('ajuste_motivos', editId, body) : inv.create('ajuste_motivos', body);
    },
    onSuccess: () => { toast.success('Motivo guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['inv-ajuste-motivos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => inv.remove('ajuste_motivos', id),
    onSuccess: () => { toast.success('Motivo eliminado'); void qc.invalidateQueries({ queryKey: ['inv-ajuste-motivos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Motivos de ajuste</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo motivo</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Catálogo ({motivos.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {motivos.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin motivos</td></tr>}
              {motivos.map((m) => (
                <tr key={m.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{m.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{m.codigo || '—'}</td>
                  <td className="py-2 pr-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs ${
                      m.tipo === 'entrada' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : m.tipo === 'salida' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      : 'bg-muted text-muted-foreground'}`}>{tipoLabel(m.tipo)}</span>
                  </td>
                  <td className="py-2 pr-3">{m.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(m)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${m.nombre}"?`, { peligro: true })) eliminar.mutate(m.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar motivo' : 'Nuevo motivo'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} /></Campo>
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Merma, Conteo físico, Caducidad…" /></Campo>
            <Campo label="Tipo">
              <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as AjusteMotivo['tipo'] })}>
                {TIPO_MOTIVO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
